export function $<T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document): T {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`missing element: ${sel}`);
  return el;
}

export function showScreen(id: 'landing' | 'gate' | 'play'): void {
  for (const s of document.querySelectorAll('.screen')) s.classList.remove('active');
  $(`#screen-${id}`).classList.add('active');
  document.body.classList.toggle('playing', id === 'play');
  // The launcher footer participates in normal document flow. If the page was
  // scrolled before entering the fixed game stage, preserve neither that stale
  // offset nor the scrollbar it creates behind the canvas.
  if (id === 'play') window.scrollTo(0, 0);
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1048576).toFixed(1)} MB`;
  return `${(n / 1073741824).toFixed(2)} GB`;
}

export function toast(message: string, kind: 'ok' | 'warn' | 'bad' = 'ok', ms = 4200): void {
  const host = document.getElementById('toasts');
  if (!host) return;
  const el = document.createElement('div');
  el.className = `toast ${kind === 'ok' ? '' : kind}`;
  el.textContent = message;
  host.appendChild(el);
  setTimeout(() => {
    el.style.transition = 'opacity .25s';
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 260);
  }, ms);
}

/**
 * A toast the player can act on. Used where the alternative is a modal: the
 * offer to restart into a bigger framebuffer is worth making, and worth being
 * trivially ignorable, because ignoring it costs only map area.
 */
export function toastAction(message: string, label: string, onClick: () => void, ms = 12000): void {
  const host = document.getElementById('toasts');
  if (!host) return;
  const el = document.createElement('div');
  el.className = 'toast action';
  el.append(message + ' ');
  const btn = document.createElement('button');
  btn.className = 'tiny';
  btn.textContent = label;
  btn.addEventListener('click', () => {
    el.remove();
    onClick();
  });
  el.appendChild(btn);
  host.appendChild(el);
  setTimeout(() => el.remove(), ms);
}

export function note(container: HTMLElement, message: string, kind: 'ok' | 'warn' | 'bad' = 'warn'): void {
  const el = document.createElement('div');
  el.className = `note ${kind === 'warn' ? '' : kind}`;
  el.textContent = message;
  container.appendChild(el);
}

/**
 * A confirm that actually exists everywhere the shell runs.
 *
 * `window.confirm` is not a dependable primitive here. The desktop app's
 * embedded pane suppresses native dialogs outright — confirm() returns false
 * instantly with nothing on screen, so every gated action "does nothing" with
 * no clue why — and a real browser drops out of fullscreen to show one. The
 * shell draws its own instead.
 *
 * Deliberately danger-shaped: Cancel takes initial focus, Escape and a
 * backdrop click both mean no, and Enter is left unbound so a reflexive
 * keystroke cannot approve a deletion.
 */
export function confirmDanger(message: string): Promise<boolean> {
  // The e2e suites answer dialogs by transcript-recording stub rather than by
  // clicking pixels; this is the seam they install (see e2e-saveedit.mjs).
  const hook = (window as unknown as { __shellConfirm?: (m: string) => boolean }).__shellConfirm;
  if (typeof hook === 'function') return Promise.resolve(hook(message));
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'overlay confirm-overlay open';
    const panel = document.createElement('div');
    panel.className = 'panel confirm-panel';
    const body = document.createElement('div');
    body.className = 'body';
    const note = document.createElement('div');
    note.className = 'note bad';
    note.textContent = message;
    const row = document.createElement('div');
    row.className = 'row';
    const cancel = document.createElement('button');
    cancel.className = 'ghost';
    cancel.textContent = 'Cancel';
    const ok = document.createElement('button');
    ok.className = 'danger';
    ok.textContent = 'Confirm';
    const spacer = document.createElement('span');
    spacer.className = 'spacer';
    row.append(cancel, spacer, ok);
    body.append(note, row);
    panel.appendChild(body);
    overlay.appendChild(panel);

    const done = (value: boolean) => {
      document.removeEventListener('keydown', onKey, true);
      overlay.remove();
      resolve(value);
    };
    // Capture phase: the game's window-level key handler (and the shell's own
    // hotkeys) must not also act on the keystroke that answered the dialog.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        done(false);
      }
    };
    document.addEventListener('keydown', onKey, true);
    cancel.addEventListener('click', () => done(false));
    ok.addEventListener('click', () => done(true));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) done(false);
    });

    document.body.appendChild(overlay);
    cancel.focus();
  });
}
